import { useMemo } from "react";
import {
    supportedTimezones,
    timezoneLabel,
} from "../../domain/timezones";

interface TimezoneSelectProps {
    /** The stored IANA identifier, or "" when none has been chosen. */
    value: string;
    onChange: (timezone: string) => void;
    /** Shown as the empty option; the caller supplies the translated words. */
    placeholder: string;
    id?: string;
    /** Form field name, so end-to-end tests can address it. */
    name?: string;
    className?: string;
    disabled?: boolean;
    "aria-label"?: string;
}

/**
 * An IANA timezone picker with no opinion about whose zone it is.
 *
 * Deliberately allows the empty value: an unset patient timezone is not a
 * missing answer, it is the answer "nobody has said", which reads downstream as
 * the therapist's own zone. A picker that forced a choice would turn a known
 * unknown into a guess stored as fact.
 */
export default function TimezoneSelect({
    value,
    onChange,
    placeholder,
    id,
    name,
    className = "",
    disabled = false,
    "aria-label": ariaLabel,
}: TimezoneSelectProps) {
    const zones = useMemo(() => supportedTimezones(), []);

    // A stored zone this runtime does not enumerate must still be selectable,
    // otherwise merely opening the form would change the record.
    const options = useMemo(
        () => (!value || zones.includes(value) ? zones : [value, ...zones]),
        [zones, value]
    );

    return (
        <select
            id={id}
            name={name}
            aria-label={ariaLabel}
            className={className}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
        >
            <option value="">{placeholder}</option>
            {options.map((zone) => (
                <option key={zone} value={zone}>
                    {timezoneLabel(zone)}
                </option>
            ))}
        </select>
    );
}
